import { createQueue } from "@proj-airi/stream-kit";
import rehypeParse from "rehype-parse";
import rehypeStringify from "rehype-stringify";
import { unified } from "unified";
import { visit } from "unist-util-visit";
import { APICallError } from "@xsai/shared";
import { stepCountAtLeast } from "@xsai/shared-chat";
import { streamText } from "@xsai/stream-text";
import { errorMessageFrom } from "@moeru/std";
import { nanoid } from "nanoid";
import { responses } from "@xsai-ext/responses";
//#region src/messages/render-context.ts
function renderHistoryAction(item) {
	if (item.type === "summary") return [
		"Summary:",
		item.text,
		item.fromTurnIndex != null || item.toTurnIndex != null ? `Window: ${item.fromTurnIndex ?? "?"} -> ${item.toTurnIndex ?? "?"}.` : void 0
	].filter(Boolean).join("\n");
	if (item.type === "reaction") return `${item.reactionType}: ${item.text}`;
	if (item.type === "domain-event") return [`Domain event: ${item.eventType}`, JSON.stringify(item.payload, null, 2)].join("\n");
	if (item.action.kind === "text") return item.action.text;
	if (item.action.kind === "event") return `${item.action.name}${item.action.payload ? ` ${JSON.stringify(item.action.payload)}` : ""}`;
	if (item.action.kind === "move-played" || item.action.kind === "move-executed") return `${item.action.kind} ${item.action.san}`;
	return JSON.stringify(item.action);
}
/** Renders domain context as text without assigning a provider role or wire format. */
function renderSegmentText(segment) {
	if (segment.type === "runtime-context") return segment.entries.length ? `\n[Context]\n${segment.entries.map((entry) => `- ${entry.source}: ${entry.text}`).join("\n")}` : "";
	if (segment.type === "text") return segment.text;
	if (segment.type === "instruction") return [segment.priority ? `Instruction [${segment.priority}]:` : "Instruction:", segment.text].join("\n");
	if (segment.type === "tagged-text") return `<${segment.tag}>${segment.text}</${segment.tag}>`;
	if (segment.type === "domain-event") return [`Domain event: ${segment.eventType}`, JSON.stringify(segment.payload, null, 2)].join("\n");
	if (segment.type === "state-snapshot") return [`State snapshot: ${segment.stateType}`, JSON.stringify(segment.payload, null, 2)].join("\n");
	if (segment.type === "summary") return [
		"Summary:",
		segment.text,
		segment.metadata ? JSON.stringify(segment.metadata, null, 2) : void 0
	].filter(Boolean).join("\n");
	if (segment.type === "reference") return [`Reference: ${segment.refType} -> ${segment.targetId}`, segment.note].filter(Boolean).join("\n");
	if (segment.type === "history-block") return segment.items.map(renderHistoryAction).join("\n");
	throw new Error(`Cannot render ${segment.type} as context text`);
}
//#endregion
//#region src/messages/turns.ts
/** Decodes one known model step. Tool results correlate only with calls in this round. */
function readRound(id, entries) {
	const round = {
		id,
		content: [],
		toolInvocations: [],
		projectionIssues: []
	};
	for (const entry of entries) for (const segment of entry.segments) if (segment.type === "tool-call") {
		if (round.toolInvocations.some((call) => call.callId === segment.callId)) {
			round.projectionIssues.push(`Duplicate tool call ${segment.callId} in round ${id}`);
			continue;
		}
		const invocationId = `${id}/${segment.callId}`;
		round.content.push({
			type: "tool",
			invocationId
		});
		round.toolInvocations.push({
			id: invocationId,
			callId: segment.callId,
			name: segment.name,
			arguments: segment.arguments,
			execution: { status: "pending" }
		});
	} else if (segment.type === "tool-result") {
		const invocation = round.toolInvocations.find((call) => call.callId === segment.callId);
		if (invocation) invocation.execution = {
			status: "succeeded",
			output: segment.content
		};
		else round.projectionIssues.push(`Tool result ${segment.callId} has no call in round ${id}`);
	} else if (entry.role === "assistant") {
		if (segment.type !== "image" && segment.type !== "audio" && segment.type !== "file") round.content.push(segment);
	}
	return round;
}
/**
* Reads existing Chat-shaped history at its storage boundary. Each assistant message starts a
* round; following tool results belong to it. Runtime adapters use SDK step boundaries instead.
*/
function readTurns(entries) {
	const turns = [];
	let assistant;
	let pending = [];
	function flush() {
		if (assistant && pending.length) {
			assistant.rounds.push(readRound(`${assistant.id}/${assistant.rounds.length}`, pending));
			pending = [];
		}
	}
	for (const entry of entries) {
		if (entry.role === "assistant" || entry.role === "tool") {
			if (!assistant) {
				assistant = {
					type: "assistant",
					id: entry.id,
					status: "completed",
					rounds: []
				};
				turns.push(assistant);
			}
			if (entry.role === "assistant") flush();
			pending.push(entry);
			continue;
		}
		flush();
		assistant = void 0;
		if (entry.role === "user") turns.push({
			type: "user",
			id: entry.id,
			content: entry.segments
		});
		else turns.push({
			type: "system",
			id: entry.id,
			authority: entry.role === "system" || entry.role === "developer" ? entry.role : "context",
			content: entry.segments
		});
	}
	flush();
	return turns;
}
/** Expands one round for a protocol adapter without duplicating stored calls or results. */
function projectRound(round) {
	if (round.projectionIssues.length) throw new Error(`Round ${round.id} cannot be projected: ${round.projectionIssues.join("; ")}`);
	const entries = [];
	const segments = [];
	for (const part of round.content) {
		if (part.type !== "tool") {
			segments.push(part);
			continue;
		}
		const invocation = round.toolInvocations.find((call) => call.id === part.invocationId);
		if (!invocation) throw new Error(`Missing tool invocation ${part.invocationId}`);
		segments.push({
			type: "tool-call",
			callId: invocation.callId,
			name: invocation.name,
			arguments: invocation.arguments
		});
	}
	if (segments.length) entries.push({
		id: round.id,
		role: "assistant",
		segments
	});
	for (const invocation of round.toolInvocations) if (invocation.execution.status === "succeeded" || invocation.execution.status === "failed") entries.push({
		id: invocation.id,
		role: "tool",
		segments: [{
			type: "tool-result",
			callId: invocation.callId,
			content: invocation.execution.output
		}]
	});
	return entries;
}
/** Application context becomes user-level data; trusted instructions retain their declared authority. */
function projectInput(turn) {
	if (turn.type === "user") return {
		id: turn.id,
		role: "user",
		segments: turn.content
	};
	return {
		id: turn.id,
		role: turn.authority === "context" ? "context" : turn.authority,
		segments: turn.content
	};
}
//#endregion
//#region src/messages/chat-completions.ts
/**
* Converts Chat content at storage and SDK boundaries without inventing a message envelope.
*
* @example
* chatContentToInputSegments('hello')
* // => [{ type: 'text', text: 'hello' }]
*/
function chatContentToInputSegments(content) {
	if (content == null) return [];
	if (typeof content === "string") return [{
		type: "text",
		text: content
	}];
	return content.map((part) => {
		switch (part.type) {
			case "text": return {
				type: "text",
				text: part.text
			};
			case "image_url": return {
				type: "image",
				url: part.image_url.url,
				detail: part.image_url.detail
			};
			case "input_audio": return {
				type: "audio",
				...part.input_audio
			};
			case "file":
				if (part.file.file_data !== void 0 && part.file.file_id === void 0) return {
					type: "file",
					data: part.file.file_data,
					name: part.file.filename
				};
				if (part.file.file_id !== void 0 && part.file.file_data === void 0) return {
					type: "file",
					providerFileId: part.file.file_id,
					name: part.file.filename
				};
				throw new Error("Chat file requires exactly one source");
		}
		throw new Error("Unsupported Chat content part");
	});
}
/**
* Reads Chat-shaped storage or SDK output into portable message semantics.
* This is an ingress boundary; Responses never calls the Chat request renderer.
*
* @example
* chatMessagesToProjectionEntries([{ role: 'user', content: 'Hello' }])[0].segments
* // => [{ type: 'text', text: 'Hello' }]
*/
function chatMessagesToProjectionEntries(messages, idPrefix = "message") {
	return messages.map((message, index) => {
		const id = `${idPrefix}-${index}`;
		if (message.role === "error") return {
			id,
			role: "user",
			segments: [{
				type: "text",
				text: `User encountered error: ${message.content}`
			}]
		};
		if (message.role === "tool") return {
			id,
			role: "tool",
			segments: [{
				type: "tool-result",
				callId: message.tool_call_id,
				content: chatContentToInputSegments(message.content)
			}]
		};
		if (message.role === "assistant") {
			const segments = typeof message.content === "string" ? [{
				type: "text",
				text: message.content
			}] : message.content?.map((part) => part.type === "text" ? {
				type: "text",
				text: part.text
			} : {
				type: "refusal",
				text: part.refusal
			}) ?? [];
			if (message.refusal) segments.push({
				type: "refusal",
				text: message.refusal
			});
			for (const call of message.tool_calls ?? []) {
				if (call.function.name == null || call.function.arguments == null) throw new Error("Cannot replay an unfinished function call");
				segments.push({
					type: "tool-call",
					callId: call.id,
					name: call.function.name,
					arguments: call.function.arguments
				});
			}
			return {
				id,
				role: "assistant",
				segments
			};
		}
		if (message.role === "system" || message.role === "developer") return {
			id,
			role: message.role,
			segments: typeof message.content === "string" ? [{
				type: "text",
				text: message.content
			}] : message.content.map((part) => ({
				type: "text",
				text: part.text
			}))
		};
		return {
			id,
			role: message.role,
			segments: chatContentToInputSegments(message.content)
		};
	});
}
/** Reads persisted Chat records; the storage identity scopes turn, round, and invocation ids. */
function chatMessagesToTurns(messages, idPrefix) {
	return readTurns(chatMessagesToProjectionEntries(messages, idPrefix));
}
function writeContent(segment) {
	switch (segment.type) {
		case "image": return {
			type: "image_url",
			image_url: {
				url: segment.url,
				detail: segment.detail
			}
		};
		case "audio": return {
			type: "input_audio",
			input_audio: {
				data: segment.data,
				format: segment.format
			}
		};
		case "file":
			if (segment.url) throw new Error("Chat Completions does not support file URLs");
			return {
				type: "file",
				file: {
					file_data: segment.data,
					filename: segment.name,
					file_id: segment.providerFileId
				}
			};
		default: return {
			type: "text",
			text: renderSegmentText(segment)
		};
	}
}
/**
* Converts Chat content for providers that accept only strings.
*
* @example
* chatContentToString([{ type: 'text', text: 'Hi' }, { type: 'refusal', refusal: 'No' }])
* // => 'HiNo'
*/
function chatContentToString(content) {
	return content.map((part) => part.type === "text" ? part.text : part.type === "refusal" ? part.refusal : "").join("");
}
function renderEntry(message, supportsContentArray) {
	const result = [];
	const role = message.role === "context" || message.role === "event" || message.role === "summary" ? "user" : message.role;
	let parts = [];
	let assistantParts = [];
	let calls = [];
	function flush() {
		if (role === "assistant") {
			if (assistantParts.length || calls.length) {
				const content = !supportsContentArray || assistantParts.every((part) => part.type === "text") ? assistantParts.map((part) => part.type === "text" ? part.text : part.refusal).join("") : assistantParts;
				result.push({
					role,
					content,
					...calls.length ? { tool_calls: calls } : {}
				});
			}
		} else if (parts.length) {
			if (role === "tool") throw new Error("Tool messages require a correlated tool result");
			if (role === "system" || role === "developer") {
				if (parts.some((part) => part.type !== "text")) throw new Error(`${role} messages require text content`);
				result.push({
					role,
					content: parts.map((part) => part.type === "text" ? part.text : "").join("")
				});
			} else {
				const content = !supportsContentArray || parts.every((part) => part.type === "text") ? parts.map((part) => part.type === "text" ? part.text : "").join("") : parts;
				result.push({
					role,
					content
				});
			}
		}
		parts = [];
		assistantParts = [];
		calls = [];
	}
	for (const segment of message.segments) if (segment.type === "tool-result") {
		flush();
		const content = segment.content.map(writeContent);
		result.push({
			role: "tool",
			tool_call_id: segment.callId,
			content: !supportsContentArray || content.every((part) => part.type === "text") ? content.map((part) => part.type === "text" ? part.text : "").join("") : content
		});
	} else if (segment.type === "tool-call") {
		if (role !== "assistant") throw new Error("Only assistant messages can invoke tools");
		calls.push({
			type: "function",
			id: segment.callId,
			function: {
				name: segment.name,
				arguments: segment.arguments
			}
		});
	} else {
		if (calls.length) flush();
		if (role === "assistant") {
			if (segment.type === "refusal") assistantParts.push({
				type: "refusal",
				refusal: segment.text
			});
			else assistantParts.push({
				type: "text",
				text: renderSegmentText(segment)
			});
		} else parts.push(writeContent(segment));
	}
	flush();
	return result;
}
/**
* Projects context directly into Chat Completions messages.
* Array compatibility applies only here. Only matching Chat continuation can bypass portable projection.
* Pure-text arrays become strings. String-only mode also preserves refusal text and omits media.
* Input messages and provider extension fields remain unchanged.
*/
function conversationToChatMessages(conversation, supportsContentArray = true, scope) {
	return conversation.turns.flatMap((turn) => {
		if (turn.type !== "assistant") return renderEntry(projectInput(turn), supportsContentArray);
		return turn.rounds.flatMap((round) => {
			const continuation = round.continuation;
			if (scope && continuation?.protocol === "chat-completions" && continuation.scope === scope) {
				if (!Array.isArray(continuation.data)) throw new Error("Chat continuation must contain a message array");
				return continuation.data.map((message) => {
					if (Array.isArray(message.content) && (!supportsContentArray || message.content.every((part) => part.type === "text"))) return {
						...message,
						content: chatContentToString(message.content)
					};
					return message;
				});
			}
			return projectRound(round).flatMap((entry) => renderEntry(entry, supportsContentArray));
		});
	});
}
//#endregion
//#region src/messages/context-prompt.ts
/**
* Render runtime context modules into a compact, readable text block.
*
* Use when:
* - Composing chat prompts that need to attach side-channel runtime context
*   (e.g. game state, system status) to the latest user message.
*
* Expects:
* - A snapshot keyed by `contextId`. Only the per-message `text` field is
*   included; volatile metadata (random IDs, ms timestamps) is excluded so
*   the output stays deterministic and KV-cache-friendly.
*
* Returns:
* - Empty string when the snapshot is empty.
* - Otherwise a `[Context]` block with one bullet per module, e.g.
*   `[Context]\n- system:minecraft-integration: Bot is online ...`
*
* Why this shape (not XML):
* - Weak local models (8B/14B) tend to mirror conspicuous structured
*   wrappers (`<context>...</context>`) back into their replies, treating
*   them as data to be quoted. A flat bullet list looks like ordinary
*   narrative, which suppresses that mirroring tendency.
* - See: https://github.com/moeru-ai/airi/issues/1539
*/
function formatContextPromptText(contextsSnapshot) {
	const entries = Object.entries(contextsSnapshot);
	if (entries.length === 0) return "";
	const lines = entries.flatMap(([contextId, messages]) => messages.map((m) => `- ${contextId}: ${m.text}`));
	if (lines.length === 0) return "";
	return ["[Context]", ...lines].join("\n");
}
/**
* Builds a user-role context prompt message from active runtime context.
*
* Use when:
* - A caller needs the historical standalone context prompt shape.
*
* Expects:
* - Context messages have already been bucketed and cloned by the context registry.
*
* Returns:
* - `null` when no prompt text is available.
* - A user message carrying the rendered context text otherwise.
*/
function buildContextPromptMessage(contextsSnapshot) {
	const promptText = formatContextPromptText(contextsSnapshot);
	if (!promptText) return null;
	return {
		role: "user",
		content: [{
			type: "text",
			text: promptText
		}]
	};
}
//#endregion
//#region src/messages/datetime-prefix.ts
function padDatePart(value) {
	return value.toString().padStart(2, "0");
}
/**
* Formats a timestamp as `[YYYY-MM-DD HH:MM] ` in the user's local timezone.
*
* Use when:
* - Annotating user messages so the model has a concrete time anchor on
*   every turn — historic and current user turns use the same shape so that
*   prefix-cache stays valid when a "current" turn becomes "historic" on
*   the next send.
* - Not used for assistant messages — that caused the model to mirror the
*   prefix into its own output.
*
* Returns:
* - String including a trailing space, e.g. `"[2026-04-25 18:47] "`.
*
* Before:
* - createdAt = 1745570820000  (a Unix ms in Asia/Shanghai)
*
* After:
* - "[2026-04-25 18:47] "
*/
function formatTimePrefix(createdAt) {
	const date = new Date(createdAt);
	return `[${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(date.getDate())} ${padDatePart(date.getHours())}:${padDatePart(date.getMinutes())}] `;
}
//#endregion
//#region src/messages/preview.ts
function describeSegment(segment) {
	switch (segment.type) {
		case "tool-call": return `${segment.name}(${segment.arguments})`;
		case "tool-result": return segment.content.map(describeSegment).join("");
		case "image": return "[Image]";
		case "audio": return "[Audio]";
		case "file": return `[File: ${segment.name ?? "attachment"}]`;
		case "refusal": return segment.text;
		default: return renderSegmentText(segment);
	}
}
/**
* Builds text-only hook and devtools records, excluding native state and media payloads.
* Media becomes a label, including media in tool results. These records must not feed inference.
*/
function renderConversationPreview(context) {
	return context.turns.flatMap((turn) => {
		if (turn.type !== "assistant") {
			const entry = projectInput(turn);
			return [{
				role: entry.role === "system" || entry.role === "developer" ? entry.role : "user",
				content: entry.segments.map(describeSegment).join("")
			}];
		}
		return turn.rounds.map((round) => ({
			role: "assistant",
			content: round.content.map((part) => {
				if (part.type !== "tool") return describeSegment(part);
				const invocation = round.toolInvocations.find((call) => call.id === part.invocationId);
				if (!invocation) return "[Unknown tool]";
				const output = invocation.execution.status === "succeeded" || invocation.execution.status === "failed" ? invocation.execution.output.map(describeSegment).join("") : `[${invocation.execution.status}]`;
				return `${invocation.name}(${invocation.arguments}): ${output}`;
			}).join("")
		}));
	});
}
//#endregion
//#region src/runtime/agent-hooks.ts
function createChatHooks() {
	const onBeforeMessageComposedHooks = [];
	const onAfterMessageComposedHooks = [];
	const onBeforeSendHooks = [];
	const onAfterSendHooks = [];
	const onTokenLiteralHooks = [];
	const onTokenSpecialHooks = [];
	const onStreamEndHooks = [];
	const onAssistantResponseEndHooks = [];
	const onAssistantMessageHooks = [];
	const onChatTurnCompleteHooks = [];
	function onBeforeMessageComposed(cb) {
		onBeforeMessageComposedHooks.push(cb);
		return () => {
			const index = onBeforeMessageComposedHooks.indexOf(cb);
			if (index >= 0) onBeforeMessageComposedHooks.splice(index, 1);
		};
	}
	function onAfterMessageComposed(cb) {
		onAfterMessageComposedHooks.push(cb);
		return () => {
			const index = onAfterMessageComposedHooks.indexOf(cb);
			if (index >= 0) onAfterMessageComposedHooks.splice(index, 1);
		};
	}
	function onBeforeSend(cb) {
		onBeforeSendHooks.push(cb);
		return () => {
			const index = onBeforeSendHooks.indexOf(cb);
			if (index >= 0) onBeforeSendHooks.splice(index, 1);
		};
	}
	function onAfterSend(cb) {
		onAfterSendHooks.push(cb);
		return () => {
			const index = onAfterSendHooks.indexOf(cb);
			if (index >= 0) onAfterSendHooks.splice(index, 1);
		};
	}
	function onTokenLiteral(cb) {
		onTokenLiteralHooks.push(cb);
		return () => {
			const index = onTokenLiteralHooks.indexOf(cb);
			if (index >= 0) onTokenLiteralHooks.splice(index, 1);
		};
	}
	function onTokenSpecial(cb) {
		onTokenSpecialHooks.push(cb);
		return () => {
			const index = onTokenSpecialHooks.indexOf(cb);
			if (index >= 0) onTokenSpecialHooks.splice(index, 1);
		};
	}
	function onStreamEnd(cb) {
		onStreamEndHooks.push(cb);
		return () => {
			const index = onStreamEndHooks.indexOf(cb);
			if (index >= 0) onStreamEndHooks.splice(index, 1);
		};
	}
	function onAssistantResponseEnd(cb) {
		onAssistantResponseEndHooks.push(cb);
		return () => {
			const index = onAssistantResponseEndHooks.indexOf(cb);
			if (index >= 0) onAssistantResponseEndHooks.splice(index, 1);
		};
	}
	function onAssistantMessage(cb) {
		onAssistantMessageHooks.push(cb);
		return () => {
			const index = onAssistantMessageHooks.indexOf(cb);
			if (index >= 0) onAssistantMessageHooks.splice(index, 1);
		};
	}
	function onChatTurnComplete(cb) {
		onChatTurnCompleteHooks.push(cb);
		return () => {
			const index = onChatTurnCompleteHooks.indexOf(cb);
			if (index >= 0) onChatTurnCompleteHooks.splice(index, 1);
		};
	}
	function clearHooks() {
		onBeforeMessageComposedHooks.length = 0;
		onAfterMessageComposedHooks.length = 0;
		onBeforeSendHooks.length = 0;
		onAfterSendHooks.length = 0;
		onTokenLiteralHooks.length = 0;
		onTokenSpecialHooks.length = 0;
		onStreamEndHooks.length = 0;
		onAssistantResponseEndHooks.length = 0;
		onAssistantMessageHooks.length = 0;
		onChatTurnCompleteHooks.length = 0;
	}
	async function emitBeforeMessageComposedHooks(message, context) {
		for (const hook of onBeforeMessageComposedHooks) await hook(message, context);
	}
	async function emitAfterMessageComposedHooks(message, context) {
		for (const hook of onAfterMessageComposedHooks) await hook(message, context);
	}
	async function emitBeforeSendHooks(message, context) {
		for (const hook of onBeforeSendHooks) await hook(message, context);
	}
	async function emitAfterSendHooks(message, context) {
		for (const hook of onAfterSendHooks) await hook(message, context);
	}
	async function emitTokenLiteralHooks(literal, context) {
		for (const hook of onTokenLiteralHooks) await hook(literal, context);
	}
	async function emitTokenSpecialHooks(special, context) {
		for (const hook of onTokenSpecialHooks) await hook(special, context);
	}
	async function emitStreamEndHooks(context) {
		for (const hook of onStreamEndHooks) await hook(context);
	}
	async function emitAssistantResponseEndHooks(message, context) {
		for (const hook of onAssistantResponseEndHooks) await hook(message, context);
	}
	async function emitAssistantMessageHooks(message, messageText, context) {
		for (const hook of onAssistantMessageHooks) await hook(message, messageText, context);
	}
	async function emitChatTurnCompleteHooks(chat, context) {
		for (const hook of onChatTurnCompleteHooks) await hook(chat, context);
	}
	return {
		onBeforeMessageComposed,
		onAfterMessageComposed,
		onBeforeSend,
		onAfterSend,
		onTokenLiteral,
		onTokenSpecial,
		onStreamEnd,
		onAssistantResponseEnd,
		onAssistantMessage,
		onChatTurnComplete,
		emitBeforeMessageComposedHooks,
		emitAfterMessageComposedHooks,
		emitBeforeSendHooks,
		emitAfterSendHooks,
		emitTokenLiteralHooks,
		emitTokenSpecialHooks,
		emitStreamEndHooks,
		emitAssistantResponseEndHooks,
		emitAssistantMessageHooks,
		emitChatTurnCompleteHooks,
		clearHooks
	};
}
//#endregion
//#region src/runtime/tool-result-status.ts
/**
* Writes the structured terminal state onto a tool result.
* Callers still keep the provider result text. Readers use `status` and `errorCode` first.
* Results that already carry `status` stay unchanged.
*
* @example
* annotateToolCallResult({ type: 'tool-call-result', id: 'call-1', isError: true, result: 'The operation timed out.' })
* // => { type: 'tool-call-result', id: 'call-1', isError: true, result: 'The operation timed out.', status: 'timed-out', errorCode: 'timeout' }
*/
function annotateToolCallResult(result) {
	if (result.status) return result;
	const text = textFrom(result.result);
	const statusField = statusFrom(text);
	const reportedError = reportsError(text);
	const failed = result.isError === true || reportedError;
	let status = "succeeded";
	let errorCode;
	if (statusField === "requires_confirmation") status = "pending-confirmation";
	else if (failed && /timeout|timed out|aborted/i.test(text)) {
		status = "timed-out";
		errorCode = "timeout";
	} else if (failed && /reject|denied|refused|cancelled|canceled/i.test(text)) {
		status = "rejected";
		errorCode = "rejected";
	} else if (failed && /not available/i.test(text)) {
		status = "failed";
		errorCode = "unavailable";
	} else if (failed) {
		status = "failed";
		errorCode = "failed";
	}
	return {
		...result,
		status,
		...errorCode ? { errorCode } : {}
	};
}
function textFrom(content) {
	if (typeof content === "string") return content;
	if (!Array.isArray(content)) return "";
	return content.map((part) => {
		if (typeof part === "object" && part && "text" in part && typeof part.text === "string") return part.text;
		return "";
	}).join("");
}
function statusFrom(text) {
	try {
		const parsed = JSON.parse(text);
		if (typeof parsed === "object" && parsed && "status" in parsed && typeof parsed.status === "string") return parsed.status;
	} catch {
		return "";
	}
	return "";
}
function reportsError(text) {
	try {
		const parsed = JSON.parse(text);
		return typeof parsed === "object" && parsed !== null && "isError" in parsed && parsed.isError === true;
	} catch {
		return false;
	}
}
//#endregion
//#region src/runtime/llm-marker-parser.ts
const TAG_OPEN = "<|";
const TAG_CLOSE = "|>";
const ESCAPED_TAG_OPEN = "<{'|'}";
const ESCAPED_TAG_CLOSE = "{'|'}>";
function createPushStream() {
	let closed = false;
	let controller = null;
	return {
		stream: new ReadableStream({
			start(ctrl) {
				controller = ctrl;
			},
			cancel() {
				closed = true;
			}
		}),
		write(value) {
			if (!controller || closed) return;
			controller.enqueue(value);
		},
		close() {
			if (!controller || closed) return;
			closed = true;
			controller.close();
		},
		error(err) {
			if (!controller || closed) return;
			closed = true;
			controller.error(err);
		}
	};
}
async function readStream(stream, handler) {
	const reader = stream.getReader();
	try {
		while (true) {
			const { value, done } = await reader.read();
			if (done) break;
			await handler(value);
		}
	} finally {
		reader.releaseLock();
	}
}
function createLlmMarkerParser(options) {
	const minLiteralEmitLength = Math.max(1, options?.minLiteralEmitLength ?? 1);
	const tailLength = Math.max(1, 5);
	let buffer = "";
	let inTag = false;
	return {
		async consume(textPart, onLiteral, onSpecial) {
			buffer += textPart;
			buffer = buffer.replaceAll(ESCAPED_TAG_OPEN, TAG_OPEN).replaceAll(ESCAPED_TAG_CLOSE, TAG_CLOSE);
			while (buffer.length > 0) if (!inTag) {
				const openTagIndex = buffer.indexOf(TAG_OPEN);
				if (openTagIndex < 0) {
					if (buffer.length - tailLength >= minLiteralEmitLength) {
						const emit = buffer.slice(0, -5);
						buffer = buffer.slice(-5);
						await onLiteral(emit);
					}
					break;
				}
				if (openTagIndex > 0) {
					const emit = buffer.slice(0, openTagIndex);
					buffer = buffer.slice(openTagIndex);
					await onLiteral(emit);
				}
				inTag = true;
			} else {
				const closeTagIndex = buffer.indexOf(TAG_CLOSE);
				if (closeTagIndex < 0) break;
				const emit = buffer.slice(0, closeTagIndex + 2);
				buffer = buffer.slice(closeTagIndex + 2);
				await onSpecial(emit);
				inTag = false;
			}
		},
		async end(onLiteral) {
			if (!inTag && buffer.length > 0) {
				await onLiteral(buffer);
				buffer = "";
			}
		}
	};
}
function createLlmMarkerStream(input, options) {
	const { stream, write, close, error } = createPushStream();
	const parser = createLlmMarkerParser(options);
	readStream(input, async (chunk) => {
		await parser.consume(chunk, async (literal) => {
			if (!literal) return;
			write({
				type: "literal",
				value: literal
			});
		}, async (special) => {
			write({
				type: "special",
				value: special
			});
		});
	}).then(async () => {
		await parser.end(async (literal) => {
			if (!literal) return;
			write({
				type: "literal",
				value: literal
			});
		});
		close();
	}).catch((err) => {
		error(err);
	});
	return stream;
}
/**
* Creates a streaming parser for LLM responses with AIRI special markers.
*
* Use when:
* - Handling streamed model output that may contain `<|...|>` markers.
* - Literal text and special marker tokens need to be emitted separately.
*
* Expects:
* - Callers feed chunks in order and call `end()` once the model stream ends.
*
* Returns:
* - A parser with `consume()` and `end()` methods.
*/
function useLlmmarkerParser(options) {
	let fullText = "";
	const { stream, write, close } = createPushStream();
	const processing = readStream(createLlmMarkerStream(stream, { minLiteralEmitLength: options.minLiteralEmitLength }), async (token) => {
		if (token.type === "literal") await options.onLiteral?.(token.value);
		if (token.type === "special") await options.onSpecial?.(token.value);
	});
	return {
		/**
		* Consumes a chunk of text from the stream.
		*
		* @param textPart The chunk of text to consume.
		*/
		async consume(textPart) {
			fullText += textPart;
			write(textPart);
		},
		/**
		* Finalizes the parsing process.
		* Any remaining content in the buffer is flushed as a final literal part.
		*/
		async end() {
			close();
			await processing;
			await options.onEnd?.(fullText);
		}
	};
}
//#endregion
//#region src/runtime/response-categoriser.ts
/**
* Maps tag names to categories
* All tags are treated as reasoning (filtered from TTS)
*/
function mapTagNameToCategory(_tagName) {
	return "reasoning";
}
/**
* Extracts all XML-like tags from a response using rehype pipeline
* Works with any tag format: <tag>content</tag>
* Only extracts tags that are actually complete (have closing tags in source)
*/
function extractAllTags(response) {
	const tags = [];
	try {
		const tree = unified().use(rehypeParse, { fragment: true }).parse(response);
		visit(tree, "element", (node) => {
			const position = node.position;
			if (!position?.start || !position?.end) return;
			const startIndex = getOffsetFromPosition(response, position.start);
			const endIndex = getOffsetFromPosition(response, position.end);
			if (startIndex === -1 || endIndex === -1) return;
			const fullMatch = response.slice(startIndex, endIndex);
			const expectedClosingTag = `</${node.tagName}>`;
			if (!fullMatch.includes(expectedClosingTag)) return;
			tags.push({
				tagName: node.tagName,
				content: extractTextContent(node),
				fullMatch,
				startIndex,
				endIndex
			});
		});
	} catch (error) {
		console.error("Failed to parse response for tag extraction:", error);
	}
	return tags;
}
/**
* Converts a position (line/column) to a character offset in the string
*/
function getOffsetFromPosition(text, position) {
	if (!position || typeof position.line !== "number" || typeof position.column !== "number") return -1;
	const lines = text.split("\n");
	let offset = 0;
	for (let i = 0; i < position.line - 1 && i < lines.length; i++) offset += lines[i].length + 1;
	offset += position.column - 1;
	return offset;
}
/**
* Extracts text content from an element node
*/
function extractTextContent(node) {
	const textParts = [];
	if (node.children) {
		for (const child of node.children) if (child.type === "text") textParts.push(child.value);
		else if (child.type === "element") textParts.push(extractTextContent(child));
	}
	return textParts.join("");
}
/**
* Categorizes a model response by dynamically extracting any XML-like tags
* Works with any tag format the model uses
*/
function categorizeResponse(response, _providerId) {
	const extractedTags = extractAllTags(response);
	if (extractedTags.length === 0) return {
		segments: [],
		speech: response,
		reasoning: "",
		raw: response
	};
	const segments = extractedTags.map((tag) => ({
		category: mapTagNameToCategory(tag.tagName),
		content: tag.content.trim(),
		startIndex: tag.startIndex,
		endIndex: tag.endIndex,
		raw: tag.fullMatch,
		tagName: tag.tagName
	}));
	segments.sort((a, b) => a.startIndex - b.startIndex);
	const speechParts = [];
	let lastEnd = 0;
	for (const segment of segments) {
		if (segment.startIndex > lastEnd) {
			const text = response.slice(lastEnd, segment.startIndex).trim();
			if (text) speechParts.push(text);
		}
		lastEnd = Math.max(lastEnd, segment.endIndex);
	}
	if (lastEnd < response.length) {
		const text = response.slice(lastEnd).trim();
		if (text) speechParts.push(text);
	}
	const reasoning = segments.filter((s) => s.category === "reasoning").map((s) => s.content).join("\n\n");
	return {
		segments,
		speech: speechParts.join(" ").trim() || "",
		reasoning,
		raw: response
	};
}
/**
* Note: This receives literal text from useLlmmarkerParser (special tokens <|...|> are already extracted).
* Only XML/HTML tags like <think>, <reasoning> need to be parsed here.
*/
function createStreamingCategorizer(providerId, onSegment) {
	let buffer = "";
	let categorized = null;
	let lastEmittedSegmentIndex = -1;
	let lastParsedLength = 0;
	let tagState = "outside";
	let tagStackDepth = 0;
	function checkIncompleteTag() {
		try {
			const tree = unified().use(rehypeParse, { fragment: true }).parse(buffer);
			const stringified = unified().use(rehypeStringify).stringify(tree).toString();
			if (stringified !== buffer) return buffer.trim().slice(-30) !== stringified.trim().slice(-30);
			return false;
		} catch {
			return true;
		}
	}
	function processChunkIncrementally(chunk) {
		let tagJustClosed = false;
		for (let i = 0; i < chunk.length; i++) {
			const char = chunk[i];
			switch (tagState) {
				case "outside":
					if (char === "<") {
						if (i + 1 < chunk.length && chunk[i + 1] === "/") {
							tagState = "in-closing-tag";
							i++;
						} else tagState = "in-opening-tag";
					}
					break;
				case "in-opening-tag":
					if (char === ">") {
						tagState = "in-content";
						tagStackDepth++;
					}
					break;
				case "in-content":
					if (char === "<") {
						if (i + 1 < chunk.length && chunk[i + 1] === "/") {
							tagState = "in-closing-tag";
							i++;
						} else tagState = "in-opening-tag";
					}
					break;
				case "in-closing-tag": if (char === ">") {
					tagStackDepth--;
					if (tagStackDepth === 0) {
						tagState = "outside";
						tagJustClosed = true;
					} else tagState = "in-content";
				}
			}
		}
		return tagJustClosed;
	}
	return {
		consume(chunk) {
			const tagJustClosed = processChunkIncrementally(chunk);
			buffer += chunk;
			if (!categorized || tagJustClosed || buffer.length - lastParsedLength > 1e3) {
				categorized = categorizeResponse(buffer, providerId);
				lastParsedLength = buffer.length;
			}
			if (!categorized) {
				categorized = categorizeResponse(buffer, providerId);
				lastParsedLength = buffer.length;
			}
			if (onSegment && categorized.segments.length > 0) for (let i = lastEmittedSegmentIndex + 1; i < categorized.segments.length; i++) {
				const segment = categorized.segments[i];
				if (buffer.length >= segment.endIndex) {
					onSegment(segment);
					lastEmittedSegmentIndex = i;
				}
			}
		},
		/**
		* Checks if the current position in the stream is part of speech content
		* Returns true if the text should be sent to TTS
		*/
		isSpeechAt(position) {
			if (!categorized || categorized.segments.length === 0) return true;
			for (const segment of categorized.segments) if (position >= segment.startIndex && position < segment.endIndex) return false;
			return true;
		},
		/**
		* Filters text to only include speech parts
		* Removes content that falls within thought/reasoning segments
		*/
		filterToSpeech(text, startPosition) {
			if (checkIncompleteTag()) {
				const fullText = buffer + text;
				try {
					const tree = unified().use(rehypeParse, { fragment: true }).parse(fullText);
					let closingOffset = -1;
					visit(tree, "element", (node) => {
						const position = node.position;
						if (position?.end && closingOffset === -1) {
							const endOffset = getOffsetFromPosition(fullText, position.end);
							const elementSource = fullText.slice(getOffsetFromPosition(fullText, position.start), endOffset);
							const expectedClosingTag = `</${node.tagName}>`;
							if (elementSource.includes(expectedClosingTag)) {
								if (endOffset >= buffer.length && endOffset <= fullText.length) closingOffset = endOffset - buffer.length;
							}
						}
					});
					if (closingOffset === -1) return "";
					text = text.slice(closingOffset);
					startPosition += closingOffset;
					categorized = categorizeResponse(buffer, providerId);
				} catch {
					return "";
				}
			}
			if (!categorized || categorized.segments.length === 0) return text;
			let filtered = "";
			const endPosition = startPosition + text.length;
			const overlappingSegments = categorized.segments.filter((segment) => segment.endIndex > startPosition && segment.startIndex < endPosition);
			if (overlappingSegments.length === 0) return text;
			let currentPos = startPosition;
			for (const segment of overlappingSegments) {
				const segmentStart = Math.max(segment.startIndex, startPosition);
				const segmentEnd = Math.min(segment.endIndex, endPosition);
				if (segmentStart > currentPos) {
					const beforeStart = currentPos - startPosition;
					const beforeEnd = segmentStart - startPosition;
					filtered += text.slice(beforeStart, beforeEnd);
				}
				currentPos = segmentEnd;
			}
			if (currentPos < endPosition) {
				const afterStart = currentPos - startPosition;
				filtered += text.slice(afterStart);
			}
			return filtered;
		},
		getCurrentPosition() {
			return buffer.length;
		},
		end() {
			return categorizeResponse(buffer, providerId);
		},
		getCurrent() {
			return categorized;
		}
	};
}
//#endregion
//#region src/runtime/chat-orchestrator-runtime.ts
const REASONING_UI_FLUSH_CHUNK_SIZE = 24;
/**
* Caps repeated reply text in the model prompt. The referenced message remains
* in history, so the prefix only needs enough text to identify it.
*/
const REPLY_PROMPT_REFERENCE_CHARACTER_LIMIT = 480;
function prependTextToContent(msg, text) {
	const content = msg.content;
	if (content === void 0) return {
		...msg,
		content: text
	};
	if (typeof content === "string") return {
		...msg,
		content: `${text}${content}`
	};
	if (Array.isArray(content)) {
		const first = content[0];
		if (first && first.type === "text" && typeof first.text === "string") {
			const next = [{
				...first,
				text: `${text}${first.text}`
			}, ...content.slice(1)];
			return {
				...msg,
				content: next
			};
		}
		return {
			...msg,
			content: [{
				type: "text",
				text
			}, ...content]
		};
	}
	return msg;
}
function getMessageText(message) {
	if (typeof message.content === "string") return message.content;
	if (!Array.isArray(message.content)) return "";
	return message.content.filter((part) => part.type === "text").map((part) => part.text).join("\n");
}
/**
* Formats a model-only reference to the message selected by the user.
*
* @example
* formatReplyPromptPrefix('message-1', new Map([
*   ['message-1', { id: 'message-1', role: 'user', content: 'Earlier turn' }],
* ]))
* // => '[Replying to: Earlier turn]\n'
*/
function formatReplyPromptPrefix(replyToMessageId, messagesById) {
	if (!replyToMessageId) return "";
	const target = messagesById.get(replyToMessageId);
	if (!target) return "";
	const targetText = getMessageText(target).replace(/\s+/g, " ").trim();
	const preview = targetText.length > REPLY_PROMPT_REFERENCE_CHARACTER_LIMIT ? `${targetText.slice(0, 479).trimEnd()}…` : targetText;
	return preview ? `[Replying to: ${preview}]\n` : `[Replying to message: ${replyToMessageId}]\n`;
}
function resolveReplyTargetId(replyToMessageId, messages) {
	if (!replyToMessageId) return void 0;
	return messages.some((message) => message.id === replyToMessageId) ? replyToMessageId : void 0;
}
function cloneStreamingMessage(message) {
	try {
		return structuredClone(message);
	} catch {
		return JSON.parse(JSON.stringify(message));
	}
}
function hasAssistantOutput(message) {
	return message.slices.length > 0 || message.tool_results.length > 0 || (message.citations?.length ?? 0) > 0 || !!message.categorization?.reasoning.trim();
}
function defaultCreateId() {
	return globalThis.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}
/**
* Creates the core chat orchestrator runtime used behind UI facades.
*
* Use when:
* - A platform wants AIRI chat send orchestration without Vue/Pinia coupling.
* - Session, context, foreground stream, and LLM integrations are provided as adapters.
*
* Expects:
* - Session messages are returned in chronological order.
* - `foregroundStream.patch` replaces the visible streaming assistant message.
*
* Returns:
* - A runtime with send queue APIs, hook registry, writable sending state, and queue snapshots.
*/
function createChatOrchestratorRuntime(deps) {
	const activeSends = /* @__PURE__ */ new Map();
	const hooks = createChatHooks();
	const now = deps.now ?? (() => Date.now());
	const monotonicNow = deps.monotonicNow ?? (() => globalThis.performance?.now?.() ?? Date.now());
	const createId = deps.createId ?? defaultCreateId;
	const unwrapMessage = deps.unwrapMessage ?? ((message) => message);
	let sending = false;
	let activeSendSessionId;
	let activeStreamingMessage;
	let pendingQueuedSends = [];
	function emitStateChange() {
		deps.onStateChange?.({
			sending,
			activeSendSessionId,
			activeStreamingMessage,
			pendingQueuedSendCount: pendingQueuedSends.length
		});
	}
	function setSending(next) {
		const nextActiveSendSessionId = next ? activeSendSessionId ?? deps.getActiveSessionId() : void 0;
		if (sending === next && activeSendSessionId === nextActiveSendSessionId) return;
		sending = next;
		activeSendSessionId = nextActiveSendSessionId;
		if (!next) activeStreamingMessage = void 0;
		emitStateChange();
	}
	function isForegroundSession(sessionId) {
		return sessionId === deps.getActiveSessionId();
	}
	function beginStream(sessionId, message) {
		sending = true;
		activeSendSessionId = sessionId;
		activeStreamingMessage = cloneStreamingMessage(message);
		emitStateChange();
		if (isForegroundSession(sessionId)) deps.foregroundStream.patch(cloneStreamingMessage(message));
	}
	function updateStream(sessionId, message) {
		if (sessionId === activeSendSessionId) {
			activeStreamingMessage = cloneStreamingMessage(message);
			emitStateChange();
		}
		if (isForegroundSession(sessionId)) deps.foregroundStream.patch(cloneStreamingMessage(message));
	}
	function resetForegroundStream(sessionId) {
		if (isForegroundSession(sessionId)) deps.foregroundStream.reset();
	}
	function ingestRuntimeContexts() {
		for (const provider of deps.runtimeContextProviders ?? []) {
			const contextMessage = provider();
			if (contextMessage) deps.context.ingest(contextMessage);
		}
	}
	function getStablePromptTimestamp(message, fallbackCreatedAt) {
		if (typeof message.createdAt === "number") return message.createdAt;
		message.createdAt = fallbackCreatedAt;
		return fallbackCreatedAt;
	}
	function buildContext(history) {
		const nowTs = now();
		const messagesById = new Map(history.flatMap((message) => message.id ? [[message.id, message]] : []));
		return { turns: history.flatMap((message, historyIndex) => {
			if (message.role === "assistant" && message.generationTranscript) return [structuredClone(unwrapMessage(message.generationTranscript))];
			const source = message.role === "user" ? prependTextToContent(unwrapMessage(message), `${formatTimePrefix(getStablePromptTimestamp(message, nowTs))}${formatReplyPromptPrefix(message.replyToMessageId, messagesById)}`) : unwrapMessage(message);
			return chatMessagesToTurns(source.role === "assistant" && source.providerTranscript?.length ? source.providerTranscript : [source], message.id ?? `history-${historyIndex}`);
		}) };
	}
	async function performSend(sendingMessage, options, generation, sessionId, abortSignal, activeProvider) {
		if (!sendingMessage && !options.attachments?.length) return;
		deps.session.ensureSession(sessionId);
		const existingSessionMessages = deps.session.getSessionMessages(sessionId);
		let replyToMessageId = resolveReplyTargetId(options.replyToMessageId, existingSessionMessages);
		const turnIndex = existingSessionMessages.filter((message) => message.role === "user").length + 1;
		const isActivationAttempt = !existingSessionMessages.some((message) => message.role === "assistant" && !message.interrupted);
		ingestRuntimeContexts();
		const sendingCreatedAt = now();
		const streamContextMessageId = createId();
		const assistantMessageId = createId();
		const roundId = createId();
		const streamingMessageContext = {
			turnId: roundId,
			message: {
				role: "user",
				content: sendingMessage,
				createdAt: sendingCreatedAt,
				id: streamContextMessageId,
				...replyToMessageId ? { replyToMessageId } : {}
			},
			contexts: deps.context.snapshot(),
			composedMessage: [],
			input: options.input
		};
		deps.onLifecycle?.({
			phase: "before-compose",
			channel: "chat",
			sessionId,
			textPreview: sendingMessage,
			details: { contexts: streamingMessageContext.contexts }
		});
		const isStaleGeneration = () => deps.session.getSessionGeneration(sessionId) !== generation;
		const shouldAbort = () => isStaleGeneration() || abortSignal.aborted;
		if (shouldAbort()) return;
		const buildingMessage = {
			role: "assistant",
			content: "",
			slices: [],
			tool_results: [],
			createdAt: now(),
			id: assistantMessageId
		};
		beginStream(sessionId, buildingMessage);
		const hasVoice = options.input?.type === "input:voice" || options.input?.type === "input:text:voice";
		const sendSource = hasVoice ? "voice" : "text";
		const correlation = {
			conversationId: sessionId,
			roundId,
			turnIndex
		};
		deps.onTrackFirstMessage?.();
		if (isActivationAttempt) deps.onChatActivationStarted?.({
			...correlation,
			source: sendSource,
			model: options.model,
			provider: activeProvider
		});
		deps.onMessageSendStarted?.({
			...correlation,
			source: sendSource,
			model: options.model
		});
		const roundStartedAt = monotonicNow();
		let assistantStored = false;
		let generationCompleted = false;
		try {
			await hooks.emitBeforeMessageComposedHooks(sendingMessage, streamingMessageContext);
			const contentParts = [{
				type: "text",
				text: sendingMessage
			}];
			if (options.attachments) {
				for (const attachment of options.attachments) if (attachment.type === "image") contentParts.push({
					type: "image_url",
					image_url: { url: `data:${attachment.mimeType};base64,${attachment.data}` }
				});
			}
			const finalContent = contentParts.length > 1 ? contentParts : sendingMessage;
			if (!streamingMessageContext.input) streamingMessageContext.input = {
				type: "input:text",
				data: { text: sendingMessage }
			};
			if (shouldAbort()) return;
			replyToMessageId = resolveReplyTargetId(options.replyToMessageId, deps.session.getSessionMessages(sessionId));
			if (replyToMessageId) streamingMessageContext.message.replyToMessageId = replyToMessageId;
			else delete streamingMessageContext.message.replyToMessageId;
			const userMessage = {
				role: "user",
				content: finalContent,
				createdAt: sendingCreatedAt,
				id: roundId,
				...replyToMessageId ? { replyToMessageId } : {},
				...options.toolReferences?.length ? { tools: options.toolReferences } : {}
			};
			deps.session.appendSessionMessage(sessionId, userMessage);
			deps.onUserMessageAppended?.({
				sessionId,
				message: userMessage,
				messageText: sendingMessage,
				source: sendSource,
				model: options.model,
				provider: activeProvider,
				roundId,
				turnIndex
			});
			const sessionMessagesForSend = deps.session.getSessionMessages(sessionId);
			deps.onUserTurnReady?.({
				messageText: sendingMessage,
				sessionMessages: sessionMessagesForSend
			});
			const categorizer = createStreamingCategorizer(deps.getActiveProvider());
			let streamPosition = 0;
			const parser = useLlmmarkerParser({
				onLiteral: async (literal) => {
					if (shouldAbort()) return;
					categorizer.consume(literal);
					const speechOnly = categorizer.filterToSpeech(literal, streamPosition);
					streamPosition += literal.length;
					if (speechOnly.trim()) {
						buildingMessage.content += speechOnly;
						await hooks.emitTokenLiteralHooks(speechOnly, streamingMessageContext);
						const lastSlice = buildingMessage.slices.at(-1);
						if (lastSlice?.type === "text") lastSlice.text += speechOnly;
						else buildingMessage.slices.push({
							type: "text",
							text: speechOnly
						});
						updateStream(sessionId, buildingMessage);
					}
				},
				onSpecial: async (special) => {
					if (shouldAbort()) return;
					await hooks.emitTokenSpecialHooks(special, streamingMessageContext);
				},
				onEnd: async (fullText) => {
					if (isStaleGeneration()) return;
					const finalCategorization = categorizeResponse(fullText, deps.getActiveProvider());
					const reasoningContentField = buildingMessage.categorization?.reasoning?.trim();
					buildingMessage.categorization = {
						speech: finalCategorization.speech,
						reasoning: reasoningContentField || finalCategorization.reasoning
					};
					updateStream(sessionId, buildingMessage);
				},
				minLiteralEmitLength: 1
			});
			const toolCallQueue = createQueue({ handlers: [async (ctx) => {
				if (shouldAbort()) return;
				if (ctx.data.type === "tool-call") {
					buildingMessage.slices.push(ctx.data);
					updateStream(sessionId, buildingMessage);
					return;
				}
				if (ctx.data.type === "tool-call-result") {
					buildingMessage.tool_results.push(ctx.data);
					updateStream(sessionId, buildingMessage);
				}
			}] });
			const context = buildContext(sessionMessagesForSend);
			const systemPromptSupplement = deps.getSystemPromptSupplement?.()?.trim();
			if (systemPromptSupplement) {
				const systemMessage = context.turns.find((turn) => turn.type === "system" && turn.authority === "system");
				if (systemMessage?.type === "system") systemMessage.content.push({
					type: "text",
					text: `\n\n${systemPromptSupplement}`
				});
				else context.turns.unshift({
					id: "system-supplement",
					type: "system",
					authority: "system",
					content: [{
						type: "text",
						text: systemPromptSupplement
					}]
				});
			}
			const contextsSnapshot = deps.context.snapshot();
			const entries = Object.entries(contextsSnapshot).flatMap(([source, messages]) => messages.map((message) => ({
				source,
				text: message.text
			})));
			if (entries.length) {
				const lastMessage = context.turns.at(-1);
				if (lastMessage?.type === "user") lastMessage.content.push({
					type: "runtime-context",
					entries
				});
				deps.onLifecycle?.({
					phase: "prompt-context-built",
					channel: "chat",
					sessionId,
					details: { contexts: contextsSnapshot }
				});
			}
			streamingMessageContext.composedMessage = renderConversationPreview(context);
			deps.onPromptProjection?.({
				sessionId,
				message: sendingMessage,
				contexts: contextsSnapshot,
				composedMessage: streamingMessageContext.composedMessage
			});
			deps.onLifecycle?.({
				phase: "after-compose",
				channel: "chat",
				sessionId,
				textPreview: sendingMessage,
				details: { composedMessage: streamingMessageContext.composedMessage }
			});
			await hooks.emitAfterMessageComposedHooks(sendingMessage, streamingMessageContext);
			await hooks.emitBeforeSendHooks(sendingMessage, streamingMessageContext);
			let fullText = "";
			const headers = options.providerConfig?.headers || {};
			if (shouldAbort()) return;
			const llmRequestStartedAt = monotonicNow();
			let llmFirstTokenEmitted = false;
			let generationUsage = { source: "unavailable" };
			let generatedTurn;
			deps.onLlmRequestStarted?.({
				...correlation,
				model: options.model,
				provider: deps.getActiveProvider() || "unknown",
				hasVoice
			});
			await deps.llm.stream(options.model, options.chatProvider, context, {
				headers,
				providerId: activeProvider,
				abortSignal,
				onGeneratedTurn: (turn) => {
					generatedTurn = structuredClone(turn);
				},
				requestCorrelation: {
					conversationId: correlation.conversationId,
					turnId: correlation.roundId
				},
				tools: options.tools,
				temperature: options.temperature,
				topP: options.topP,
				waitForTools: true,
				onUsage: (usage) => {
					if (shouldAbort()) return;
					generationUsage = usage;
					deps.onLlmGeneration?.({
						...correlation,
						model: options.model,
						provider: activeProvider,
						inputTokens: usage.inputTokens,
						outputTokens: usage.outputTokens,
						totalTokens: usage.totalTokens,
						usageSource: usage.source
					});
				},
				onStreamEvent: async (event) => {
					if (shouldAbort()) return;
					switch (event.type) {
						case "search":
							buildingMessage.search = {
								id: event.id,
								status: event.status
							};
							updateStream(sessionId, buildingMessage);
							break;
						case "citations":
							buildingMessage.citations = [...buildingMessage.citations ?? [], ...event.citations];
							updateStream(sessionId, buildingMessage);
							break;
						case "tool-call":
							toolCallQueue.enqueue({
								type: "tool-call",
								toolCall: event
							});
							break;
						case "tool-result":
							toolCallQueue.enqueue(annotateToolCallResult({
								type: "tool-call-result",
								id: event.toolCallId,
								result: event.result
							}));
							break;
						case "tool-error":
							toolCallQueue.enqueue(annotateToolCallResult({
								type: "tool-call-result",
								id: event.toolCallId,
								isError: true,
								result: event.result
							}));
							break;
						case "text-delta":
							if (!llmFirstTokenEmitted) {
								llmFirstTokenEmitted = true;
								deps.onLlmFirstToken?.({
									...correlation,
									model: options.model,
									ttfbMs: Math.round(monotonicNow() - llmRequestStartedAt)
								});
							}
							fullText += event.text;
							await parser.consume(event.text);
							break;
						case "reasoning-delta": {
							if (shouldAbort()) return;
							const { reasoning = "" } = buildingMessage.categorization ?? {};
							const nextReasoning = reasoning + event.text;
							buildingMessage.categorization = {
								speech: typeof buildingMessage.content === "string" ? buildingMessage.content : "",
								reasoning: nextReasoning
							};
							const crossesBoundary = Math.floor(nextReasoning.length / REASONING_UI_FLUSH_CHUNK_SIZE) > Math.floor(reasoning.length / REASONING_UI_FLUSH_CHUNK_SIZE);
							if (!reasoning || crossesBoundary) updateStream(sessionId, buildingMessage);
							break;
						}
						case "finish": break;
						case "error": throw event.error ?? /* @__PURE__ */ new Error("Stream error");
					}
				}
			});
			if (shouldAbort()) return;
			await parser.end();
			if (shouldAbort()) return;
			generationCompleted = true;
			buildingMessage.generationTranscript = generatedTurn;
			try {
				deps.onAssistantResponseRendered?.({
					...correlation,
					model: options.model,
					latencyMs: Math.round(monotonicNow() - llmRequestStartedAt)
				});
			} catch (error) {
				console.error("Assistant response observer failed:", error);
			}
			if (!shouldAbort() && (buildingMessage.slices.length > 0 || generatedTurn?.rounds.length)) {
				const finalAssistant = buildingMessage;
				deps.session.appendSessionMessage(sessionId, finalAssistant);
				assistantStored = true;
				deps.onAssistantMessageAppended?.({
					sessionId,
					message: finalAssistant,
					messageText: fullText
				});
			}
			if (shouldAbort()) return;
			await hooks.emitStreamEndHooks(streamingMessageContext);
			if (shouldAbort()) return;
			await hooks.emitAssistantResponseEndHooks(fullText, streamingMessageContext);
			if (shouldAbort()) return;
			await hooks.emitAfterSendHooks(sendingMessage, streamingMessageContext);
			if (shouldAbort()) return;
			await hooks.emitAssistantMessageHooks({ ...buildingMessage }, fullText, streamingMessageContext);
			if (shouldAbort()) return;
			await hooks.emitChatTurnCompleteHooks({
				output: { ...buildingMessage },
				outputText: fullText,
				toolCalls: sessionMessagesForSend.filter((msg) => msg.role === "tool")
			}, streamingMessageContext);
			if (shouldAbort()) return;
			deps.onAssistantTurnReady?.({
				messageText: fullText,
				sessionMessages: sessionMessagesForSend
			});
			resetForegroundStream(sessionId);
			const durationMs = Math.round(monotonicNow() - roundStartedAt);
			deps.onMessageRound?.({
				...correlation,
				durationMs,
				hasVoice,
				model: options.model,
				inputTokens: generationUsage.inputTokens,
				outputTokens: generationUsage.outputTokens,
				totalTokens: generationUsage.totalTokens,
				usageSource: generationUsage.source
			});
			if (isActivationAttempt) deps.onChatActivationSucceeded?.({
				...correlation,
				durationMs,
				source: sendSource,
				model: options.model,
				provider: activeProvider
			});
		} catch (error) {
			if (shouldAbort()) return;
			if (!assistantStored && !generationCompleted && hasAssistantOutput(buildingMessage)) deps.session.appendSessionMessage(sessionId, {
				...cloneStreamingMessage(buildingMessage),
				interrupted: true
			});
			resetForegroundStream(sessionId);
			console.error("Error sending message:", error);
			deps.onMessageRoundFailed?.({
				...correlation,
				source: sendSource,
				model: options.model,
				provider: activeProvider,
				failureStage: "llm_response",
				errorCode: "llm_response_failed"
			});
			if (isActivationAttempt) deps.onChatActivationFailed?.({
				...correlation,
				source: sendSource,
				model: options.model,
				provider: activeProvider,
				failureStage: "llm_response",
				errorCode: "llm_response_failed"
			});
			throw error;
		} finally {
			if (!assistantStored && !generationCompleted && abortSignal.aborted && !isStaleGeneration() && hasAssistantOutput(buildingMessage)) {
				deps.session.appendSessionMessage(sessionId, {
					...cloneStreamingMessage(buildingMessage),
					interrupted: true
				});
				resetForegroundStream(sessionId);
			}
			setSending(false);
			deps.onSendSettled?.({ sessionId });
		}
	}
	const sendQueue = createQueue({ handlers: [async ({ data }) => {
		const { sendingMessage, options, generation, deferred, sessionId, cancelled, providerId } = data;
		if (cancelled) return;
		if (deps.session.getSessionGeneration(sessionId) !== generation) {
			deferred.reject(/* @__PURE__ */ new Error("Chat session was reset before send could start"));
			return;
		}
		const controller = new AbortController();
		activeSends.set(sessionId, controller);
		try {
			await performSend(sendingMessage, options, generation, sessionId, controller.signal, providerId);
			deferred.resolve();
		} catch (error) {
			deferred.reject(error);
		} finally {
			activeSends.delete(sessionId);
		}
	}] });
	sendQueue.on("enqueue", (queuedSend) => {
		pendingQueuedSends.push(queuedSend);
		emitStateChange();
	});
	sendQueue.on("dequeue", (queuedSend) => {
		pendingQueuedSends = pendingQueuedSends.filter((item) => item !== queuedSend);
		emitStateChange();
	});
	function ingest(sendingMessage, options, targetSessionId) {
		const sessionId = targetSessionId || deps.getActiveSessionId();
		const generation = deps.session.getSessionGeneration(sessionId);
		return new Promise((resolve, reject) => {
			sendQueue.enqueue({
				providerId: deps.getActiveProvider?.() ?? "",
				sendingMessage,
				options,
				generation,
				sessionId,
				deferred: {
					resolve,
					reject
				}
			});
		});
	}
	function cancelPendingSends(sessionId) {
		for (const [activeSessionId, controller] of activeSends) if (!sessionId || sessionId === activeSessionId) controller.abort(/* @__PURE__ */ new Error("Chat session send was cancelled"));
		for (const queued of pendingQueuedSends) {
			if (sessionId && queued.sessionId !== sessionId) continue;
			queued.cancelled = true;
			queued.deferred.reject(/* @__PURE__ */ new Error("Chat session was reset before send could start"));
		}
		pendingQueuedSends = sessionId ? pendingQueuedSends.filter((item) => item.sessionId !== sessionId) : [];
		emitStateChange();
	}
	function getPendingQueuedSendSnapshot() {
		return pendingQueuedSends.map((queued) => ({
			sessionId: queued.sessionId,
			generation: queued.generation,
			cancelled: !!queued.cancelled,
			messagePreview: queued.sendingMessage.slice(0, 120),
			hasAttachments: !!queued.options.attachments?.length,
			inputType: queued.options.input?.type
		}));
	}
	return {
		ingest,
		cancelPendingSends,
		getPendingQueuedSendSnapshot,
		getPendingQueuedSendCount: () => pendingQueuedSends.length,
		getSending: () => sending,
		setSending,
		hooks
	};
}
//#endregion
//#region src/runtime/context-registry.ts
const CONTEXT_UPDATE_REPLACE_SELF = "replace-self";
const CONTEXT_UPDATE_APPEND_SELF = "append-self";
function formatMetadataSource(source) {
	if (!source) return void 0;
	if ("extension" in source) return `${source.extension.id}:${source.id}`;
	return source.id;
}
function defaultGetSourceKey(event, fallback = "unknown") {
	return formatMetadataSource(event.metadata?.source) ?? event.source ?? fallback;
}
/**
* Creates a context registry that owns active buckets and bounded ingest history.
*
* Use when:
* - Runtime contexts need replace-self or append-self bucket semantics.
* - UI or transport layers need cloned snapshots without owning mutation policy.
*
* Expects:
* - Context messages are structured-cloneable before they enter the registry.
* - Unknown strategies should still be recorded in history for observability.
*
* Returns:
* - A registry whose snapshots cannot mutate internal active bucket state.
*/
function createContextRegistry(options = {}) {
	const historyLimit = options.historyLimit ?? 400;
	const getSourceKey = options.getSourceKey ?? defaultGetSourceKey;
	let currentActiveContexts = /* @__PURE__ */ new Map();
	let currentContextHistory = [];
	function ingest(envelope) {
		const sourceKey = getSourceKey(envelope);
		const safeEnvelopeToStore = structuredClone(envelope);
		if (!currentActiveContexts.has(sourceKey)) currentActiveContexts.set(sourceKey, []);
		let result;
		if (envelope.strategy === CONTEXT_UPDATE_REPLACE_SELF) {
			currentActiveContexts.set(sourceKey, [safeEnvelopeToStore]);
			result = {
				sourceKey,
				mutation: "replace",
				entryCount: currentActiveContexts.get(sourceKey)?.length ?? 0
			};
		} else if (envelope.strategy === CONTEXT_UPDATE_APPEND_SELF) {
			currentActiveContexts.get(sourceKey)?.push(safeEnvelopeToStore);
			result = {
				sourceKey,
				mutation: "append",
				entryCount: currentActiveContexts.get(sourceKey)?.length ?? 0
			};
		}
		currentContextHistory = [...currentContextHistory, {
			...safeEnvelopeToStore,
			sourceKey
		}].slice(-historyLimit);
		return result;
	}
	function reset() {
		currentActiveContexts = /* @__PURE__ */ new Map();
		currentContextHistory = [];
	}
	function snapshot() {
		return Object.fromEntries(Array.from(currentActiveContexts, ([sourceKey, messages]) => [sourceKey, structuredClone(messages)]));
	}
	return {
		ingest,
		reset,
		snapshot,
		activeContexts: snapshot,
		contextHistory: () => structuredClone(currentContextHistory)
	};
}
//#endregion
//#region src/runtime/generation.ts
/** A generation is owned by its caller's turn; anonymous callers receive an independent identity. */
function createAssistantTurn(turnId, runId) {
	return {
		type: "assistant",
		id: turnId ?? nanoid(),
		runId,
		status: "completed",
		rounds: []
	};
}
/**
* Saves the native step even when its portable content is unknown. A later protocol change reports
* projectionIssues instead of silently omitting data. SDK tool failures stay attached to their call.
*/
function recordRound(turn, native, step, model, roundOffset, decode) {
	const id = `${turn.id}/${roundOffset + turn.rounds.length}`;
	const entries = [];
	const issues = [];
	for (const item of native.data) try {
		entries.push(...decode(item));
	} catch (error) {
		issues.push(errorMessageFrom(error) ?? "Unknown native content");
	}
	const round = readRound(id, entries);
	round.projectionIssues.push(...issues);
	round.modelCall = {
		model,
		finishReason: step.finishReason,
		usage: step.usage
	};
	round.continuation = native;
	for (const result of step.toolResults) {
		const invocation = round.toolInvocations.find((call) => call.callId === result.toolCallId);
		if (invocation && result.isError && invocation.execution.status === "succeeded") invocation.execution = {
			...invocation.execution,
			status: "failed"
		};
	}
	turn.rounds.push(round);
	return round;
}
/**
* Owns step boundaries and produces a turn only after all model and tool steps settle.
* Each adapter supplies its native envelope and portable projection policy.
*/
function createGeneration(input) {
	const starts = [];
	const models = [];
	/** SDK snapshots mark step starts; only offsets remain owned by this generation. */
	function prepareStep({ input: current, model }) {
		starts.push(current.length);
		models.push(model ?? input.model);
		return {};
	}
	async function complete(items, steps) {
		const [final, completedSteps] = await Promise.all([items, steps]);
		const turn = createAssistantTurn(input.turnId, input.runId);
		for (const [index, step] of completedSteps.entries()) {
			const start = starts[index];
			if (start === void 0) throw new Error("Missing SDK model step boundary");
			const output = final.slice(start, starts[index + 1] ?? final.length);
			recordRound(turn, input.continuation(output, index), step, models[index] ?? input.model, input.roundOffset ?? 0, input.project);
		}
		const lastStep = completedSteps.at(-1);
		if ((lastStep?.finishReason === "tool-calls" || lastStep?.finishReason === "tool_calls") && lastStep.toolCalls.length > 0 && lastStep.toolResults.length === 0) throw new Error("Generation tool step limit reached");
		return turn;
	}
	return {
		prepareStep,
		complete
	};
}
//#endregion
//#region src/runtime/request-context.ts
/** Removes provider fields that the next request no longer defines. */
function replaceProviderConfig(target, previousKeys, next) {
	for (const key of previousKeys) if (!Object.hasOwn(next, key)) Reflect.deleteProperty(target, key);
	Object.assign(target, next);
	return Object.keys(next);
}
/** Builds a compatibility-cache key from the same configuration used by the request. */
function modelKey(model, { protocol, config }) {
	return `${protocol === "responses" ? "responses:" : ""}${config.baseURL}-${model}`;
}
/** Applies a caller override before the learned tool compatibility for this exact model request. */
function supportsTools(model, request, options) {
	return options?.supportsTools ?? options?.toolsCompatibility?.get(modelKey(model, request)) !== false;
}
/** Applies a caller override before the learned Chat content compatibility for this exact model request. */
function supportsContentArray(model, request, options) {
	return options?.supportsContentArray ?? options?.contentArrayCompatibility?.get(modelKey(model, request)) !== false;
}
/**
* Request overrides replace configured headers without regard to casing.
* Authorization keeps the SDK spelling so it also replaces the SDK apiKey default.
*/
function mergeRequestHeaders(configured, overrides) {
	const headers = new Headers(configured);
	new Headers(overrides).forEach((value, name) => headers.set(name, value));
	return Object.fromEntries(Array.from(headers, ([name, value]) => [name === "authorization" ? "Authorization" : name, value]));
}
/**
* Native continuation belongs to one provider identity, endpoint, model and conversation.
* Request credentials and headers do not participate in this persisted scope.
*/
function createContinuationScope(config, options) {
	return JSON.stringify([
		options?.providerId,
		String(config.baseURL),
		config.model,
		options?.requestCorrelation?.conversationId
	]);
}
//#endregion
//#region src/runtime/request-switch.ts
/** Carries completed tool rounds to a new continuation scope without persisting a partial turn. */
var RequestSwitch = class extends Error {
	next;
	partialTurn;
	constructor(next, partialTurn) {
		super("Model request scope changed");
		this.next = next;
		this.partialTurn = partialTurn;
	}
};
//#endregion
//#region src/runtime/xsai-events.ts
/**
* Maps xsAI stream events onto the AIRI {@link StreamEvent} contract.
*
* xsAI 0.5.0-beta.8 marks failed tool executions with `isError: true` on
* `tool-result.done` instead of aborting the stream, so AIRI can distinguish
* `tool-error` from `tool-result` directly from the event payload.
*/
function toAiriStreamEvent(event) {
	switch (event.type) {
		case "text.delta": return {
			type: "text-delta",
			text: event.delta
		};
		case "reasoning.delta": return {
			type: "reasoning-delta",
			text: event.delta
		};
		case "tool-call.done": return {
			...event,
			type: "tool-call"
		};
		case "tool-result.done":
			if (event.isError === true) return {
				type: "tool-error",
				toolCallId: event.toolCallId,
				result: typeof event.result === "string" ? event.result : JSON.stringify(event.result),
				isError: true
			};
			return {
				type: "tool-result",
				toolCallId: event.toolCallId,
				result: typeof event.result === "string" || Array.isArray(event.result) ? event.result : JSON.stringify(event.result)
			};
		case "error": return {
			type: "error",
			error: event.cause ?? new Error(event.message)
		};
		case "text.start":
		case "text.done":
		case "reasoning.start":
		case "reasoning.done":
		case "step.start":
		case "step.done":
		case "tool-call.start":
		case "tool-call.delta": return null;
	}
}
//#endregion
//#region src/runtime/chat-completions.ts
/** Projects one context snapshot and returns only the newly generated turn. */
function streamChatCompletions(input) {
	const messages = conversationToChatMessages(input.conversation, input.supportsContentArray, input.scope);
	const scopes = [];
	const generation = createGeneration({
		turnId: input.options?.requestCorrelation?.turnId ?? input.options?.generationTurnId,
		runId: input.options?.requestCorrelation?.runId,
		model: input.config.model,
		roundOffset: input.options?.generationRoundOffset,
		continuation: (data, index) => ({
			protocol: "chat-completions",
			scope: scopes[index] ?? input.scope,
			data
		}),
		project: (item) => chatMessagesToProjectionEntries([item])
	});
	let providerConfigKeys = Object.keys(input.config);
	const requestOptions = {
		...input.config,
		prepareStep: ({ input: current, steps }) => {
			const resolveStep = input.options?.resolveStep;
			if (!resolveStep) {
				scopes.push(input.scope);
				return generation.prepareStep({ input: current });
			}
			return (async () => {
				const firstStep = scopes.length === 0 && input.initialStep;
				const next = firstStep || await resolveStep();
				const nextRequest = firstStep ? {
					protocol: "chat-completions",
					config: input.config
				} : next.chatProvider.generation(next.model);
				const nextScope = createContinuationScope(nextRequest.config, {
					...input.options,
					providerId: next.providerId
				});
				if (nextRequest.protocol !== "chat-completions" || nextScope !== input.scope) throw new RequestSwitch(next, await generation.complete(Promise.resolve(current), Promise.resolve(steps)));
				const toolsSupported = supportsTools(next.model, nextRequest, input.options);
				providerConfigKeys = replaceProviderConfig(requestOptions, providerConfigKeys, nextRequest.config);
				Object.assign(requestOptions, {
					apiKey: nextRequest.config.apiKey,
					fetch: nextRequest.config.fetch,
					temperature: next.temperature,
					topP: next.topP,
					headers: mergeRequestHeaders(nextRequest.config.headers, next.headers),
					tools: toolsSupported && next.tools?.length ? next.tools : void 0,
					toolChoice: void 0
				});
				generation.prepareStep({
					input: current,
					model: next.model
				});
				const contentArraySupported = supportsContentArray(next.model, nextRequest, input.options);
				scopes.push(nextScope);
				if (!contentArraySupported) for (const [index, message] of current.entries()) {
					if (!Array.isArray(message.content)) continue;
					current[index] = {
						...message,
						content: chatContentToString(message.content)
					};
				}
				const systemIndex = current.findIndex((message) => message.role === "system");
				const systemMessage = current[systemIndex];
				if (systemMessage?.role === "system") current[systemIndex] = {
					...systemMessage,
					content: next.systemPrompt
				};
				else if (next.systemPrompt) current.unshift({
					role: "system",
					content: next.systemPrompt
				});
				return {
					input: current,
					model: next.model,
					toolChoice: toolsSupported ? input.options?.toolChoice : void 0
				};
			})();
		},
		abortSignal: input.options?.abortSignal,
		temperature: input.options?.temperature,
		topP: input.options?.topP,
		messages,
		headers: mergeRequestHeaders(input.config.headers, input.options?.headers),
		streamOptions: { includeUsage: true },
		stopWhen: stepCountAtLeast(10),
		tools: input.tools,
		toolChoice: input.options?.resolveStep ? void 0 : input.options?.toolChoice,
		onEvent: async (event) => {
			const mapped = toAiriStreamEvent(event);
			if (mapped) await input.onEvent(mapped);
		}
	};
	const result = streamText(requestOptions);
	const generatedTurn = generation.complete(result.messages, result.steps);
	return {
		...result,
		generatedTurn
	};
}
//#endregion
//#region src/runtime/responses.ts
function inputPart(segment) {
	switch (segment.type) {
		case "image": return {
			type: "input_image",
			image_url: segment.url,
			detail: segment.detail
		};
		case "file":
			if (segment.providerFileId) throw new Error("Responses file references require native input Items");
			return {
				type: "input_file",
				file_data: segment.data,
				filename: segment.name,
				file_url: segment.url
			};
		case "audio": throw new Error("This Responses adapter does not support audio input");
		default: return {
			type: "input_text",
			text: renderSegmentText(segment)
		};
	}
}
function renderMessage(message) {
	const items = [];
	const role = message.role === "context" || message.role === "event" || message.role === "summary" ? "user" : message.role;
	let parts = [];
	function flush() {
		if (!parts.length) return;
		if (role === "tool") throw new Error("Tool messages require a correlated tool result");
		if (role === "assistant") {
			const content = parts.map((part) => part.type === "refusal" ? {
				type: "refusal",
				refusal: part.text
			} : {
				type: "output_text",
				text: renderSegmentText(part)
			});
			items.push({
				type: "message",
				role,
				content
			});
		} else if (role === "user") {
			const content = parts.map(inputPart);
			items.push({
				type: "message",
				role,
				content: content.every((part) => part.type === "input_text") ? content.map((part) => part.text).join("") : content
			});
		} else items.push({
			type: "message",
			role,
			content: parts.map((part) => ({
				type: "input_text",
				text: renderSegmentText(part)
			}))
		});
		parts = [];
	}
	for (const segment of message.segments) if (segment.type === "tool-call") {
		if (role !== "assistant") throw new Error("Only assistant messages can invoke tools");
		flush();
		items.push({
			type: "function_call",
			call_id: segment.callId,
			name: segment.name,
			arguments: segment.arguments
		});
	} else if (segment.type === "tool-result") {
		flush();
		const content = segment.content.map(inputPart);
		items.push({
			type: "function_call_output",
			call_id: segment.callId,
			output: content.every((part) => part.type === "input_text") ? content.map((part) => part.text).join("") : content
		});
	} else parts.push(segment);
	flush();
	return items;
}
function renderConversation(conversation, scope) {
	return conversation.turns.flatMap((turn) => {
		if (turn.type !== "assistant") return renderMessage(projectInput(turn));
		return turn.rounds.flatMap((round) => {
			const continuation = round.continuation;
			if (continuation?.protocol === "responses" && continuation.scope === scope) {
				if (!Array.isArray(continuation.data)) throw new Error("Responses continuation must contain an item array");
				return continuation.data;
			}
			return projectRound(round).flatMap(renderMessage);
		});
	});
}
function readToolResultContent(content) {
	if (typeof content === "string") return [{
		type: "text",
		text: content
	}];
	return content.map((part) => {
		switch (part.type) {
			case "input_text": return {
				type: "text",
				text: part.text
			};
			case "input_image":
				if (!part.image_url) throw new Error("Responses image output requires a URL");
				return {
					type: "image",
					url: part.image_url,
					detail: part.detail ?? void 0
				};
			case "input_file":
				if (part.file_data != null && part.file_url == null) return {
					type: "file",
					data: part.file_data,
					name: part.filename ?? void 0
				};
				if (part.file_url != null && part.file_data == null) return {
					type: "file",
					url: part.file_url,
					name: part.filename ?? void 0
				};
				throw new Error("Responses file requires exactly one source");
			case "input_video": throw new Error("Video tool output is not supported by the conversation model");
		}
		throw new Error("Unsupported Responses tool output");
	});
}
function readCitations(part) {
	return part.annotations?.map((entry) => ({
		url: entry.url,
		title: entry.title,
		startIndex: entry.start_index,
		endIndex: entry.end_index
	}));
}
function readOutput(items) {
	return items.flatMap((item, index) => {
		const id = `output-${index}`;
		if (item.type === "function_call") return [{
			id,
			role: "assistant",
			segments: [{
				type: "tool-call",
				callId: item.call_id,
				name: item.name,
				arguments: item.arguments
			}]
		}];
		if (item.type === "function_call_output") return [{
			id,
			role: "tool",
			segments: [{
				type: "tool-result",
				callId: item.call_id,
				content: readToolResultContent(item.output)
			}]
		}];
		if (item.type === "message" && item.role === "assistant") return [{
			id,
			role: "assistant",
			segments: typeof item.content === "string" ? [{
				type: "text",
				text: item.content
			}] : item.content.map((part) => {
				if (part.type === "output_text") return {
					type: "text",
					text: part.text,
					citations: readCitations(part)
				};
				if (part.type === "refusal") return {
					type: "refusal",
					text: part.refusal
				};
				throw new Error("Unsupported Responses assistant content");
			})
		}];
		if (item.type === "reasoning" || item.type === "compaction" || item.type === "web_search_call") return [];
		throw new Error(`Unsupported Responses output item: ${item.type}`);
	});
}
function toolChoice(choice) {
	if (choice == null || typeof choice === "string") return choice;
	if (choice.type === "function") return {
		type: "function",
		name: choice.function.name
	};
	return {
		type: "allowed_tools",
		mode: choice.mode,
		tools: choice.tools.map((tool) => ({
			type: "function",
			name: tool.function.name
		}))
	};
}
/**
* Projects context directly into Responses and runs stateless tool steps.
* Only a fully settled generation produces a generated turn for the caller to commit.
*/
function streamResponses(input) {
	const items = renderConversation(input.conversation, input.scope);
	const scopes = [];
	const generation = createGeneration({
		turnId: input.options?.requestCorrelation?.turnId ?? input.options?.generationTurnId,
		runId: input.options?.requestCorrelation?.runId,
		model: input.config.model,
		roundOffset: input.options?.generationRoundOffset,
		continuation: (data, index) => ({
			protocol: "responses",
			scope: scopes[index] ?? input.scope,
			data
		}),
		project: (item) => readOutput([item])
	});
	let providerConfigKeys = Object.keys(input.config);
	const requestOptions = {
		...input.config,
		prepareStep: ({ input: current, steps }) => {
			const resolveStep = input.options?.resolveStep;
			if (!resolveStep) {
				scopes.push(input.scope);
				return generation.prepareStep({ input: current });
			}
			return (async () => {
				const firstStep = scopes.length === 0 && input.initialStep;
				const next = firstStep || await resolveStep();
				const nextRequest = firstStep ? {
					protocol: "responses",
					config: input.config,
					webSearch: input.webSearch ?? false
				} : next.chatProvider.generation(next.model);
				const nextScope = createContinuationScope(nextRequest.config, {
					...input.options,
					providerId: next.providerId
				});
				if (nextRequest.protocol !== "responses" || nextScope !== input.scope) throw new RequestSwitch(next, await generation.complete(Promise.resolve(current), Promise.resolve(steps)));
				const toolsSupported = supportsTools(next.model, nextRequest, input.options);
				providerConfigKeys = replaceProviderConfig(requestOptions, providerConfigKeys, nextRequest.config);
				Object.assign(requestOptions, {
					apiKey: nextRequest.config.apiKey,
					fetch: nextRequest.config.fetch,
					reasoning: nextRequest.config.reasoning,
					temperature: next.temperature,
					topP: next.topP,
					headers: mergeRequestHeaders(nextRequest.config.headers, next.headers),
					tools: toolsSupported ? nextRequest.webSearch ? [...next.tools ?? [], { type: "web_search" }] : next.tools : void 0,
					toolChoice: void 0
				});
				generation.prepareStep({
					input: current,
					model: next.model
				});
				scopes.push(nextScope);
				const systemIndex = current.findIndex((item) => item.type === "message" && item.role === "system");
				const systemMessage = current[systemIndex];
				if (systemMessage?.type === "message" && systemMessage.role === "system") current[systemIndex] = {
					...systemMessage,
					content: next.systemPrompt
				};
				else if (next.systemPrompt) current.unshift({
					type: "message",
					role: "system",
					content: next.systemPrompt
				});
				return {
					input: current,
					model: next.model,
					toolChoice: toolsSupported ? toolChoice(input.options?.toolChoice) : void 0
				};
			})();
		},
		input: items,
		store: false,
		include: ["reasoning.encrypted_content"],
		abortSignal: input.options?.abortSignal,
		temperature: input.options?.temperature,
		topP: input.options?.topP,
		headers: mergeRequestHeaders(input.config.headers, input.options?.headers),
		tools: input.webSearch ? [...input.tools ?? [], { type: "web_search" }] : input.tools,
		toolChoice: input.options?.resolveStep ? void 0 : toolChoice(input.options?.toolChoice),
		stopWhen: stepCountAtLeast(10),
		onEvent: async (event) => {
			const mapped = toAiriStreamEvent(event);
			if (mapped) await input.onEvent(mapped);
		},
		onNativeEvent: async (event) => {
			if (event.type !== "response.output_item.done" && event.type !== "response.output_item.added") return;
			const item = event.item;
			if (item?.type === "web_search_call") await input.onEvent({
				type: "search",
				id: item.id,
				status: item.status
			});
			if (event.type === "response.output_item.done" && item?.type === "message" && item.role === "assistant") for (const part of item.content) {
				if (part.type !== "output_text") continue;
				const citations = readCitations(part);
				if (citations?.length) await input.onEvent({
					type: "citations",
					citations
				});
			}
		}
	};
	const result = responses(requestOptions);
	const generatedTurn = generation.complete(result.input, result.steps);
	return {
		...result,
		generatedTurn
	};
}
//#endregion
//#region src/runtime/llm-service.ts
/**
* Automatic retry for provider requests that fail with a temporary HTTP status
* before any stream event reaches the consumer.
*/
const transientRetry = {
	/** Wait before each retry when the provider sends no usable `Retry-After`; its length caps the retry count. */
	backoffMs: [
		3e3,
		6e3,
		12e3
	],
	/** Longest server-requested `Retry-After` to wait silently; a longer one fails the turn so the user decides. */
	maxRetryAfterMs: 3e4
};
/** Returns the wait before the next attempt, or `undefined` when the failure is permanent or retries are spent. */
function transientRetryDelayMs(error, attempt) {
	if (attempt >= transientRetry.backoffMs.length || !(error instanceof APICallError)) return void 0;
	if (error.statusCode !== 408 && error.statusCode !== 429 && error.statusCode < 500) return void 0;
	const retryAfter = error.responseHeaders["retry-after"];
	if (!retryAfter) return transientRetry.backoffMs[attempt];
	const seconds = Number(retryAfter);
	const delayMs = Number.isFinite(seconds) ? seconds * 1e3 : Date.parse(retryAfter) - Date.now();
	if (Number.isNaN(delayMs)) return transientRetry.backoffMs[attempt];
	return delayMs <= transientRetry.maxRetryAfterMs ? Math.max(delayMs, 0) : void 0;
}
function waitBeforeRetry(delayMs, signal) {
	return new Promise((resolve, reject) => {
		if (signal?.aborted) {
			reject(signal.reason);
			return;
		}
		const timer = setTimeout(() => {
			signal?.removeEventListener("abort", onAbort);
			resolve();
		}, delayMs);
		signal?.addEventListener("abort", onAbort, { once: true });
		function onAbort() {
			clearTimeout(timer);
			reject(signal?.reason);
		}
	});
}
async function resolveTools(options) {
	return (typeof options?.tools === "function" ? await options.tools() : options?.tools) ?? [];
}
/** Runs the selected protocol adapter and waits for its generated turn and event consumers. */
async function streamOnce({ model, chatProvider, conversation, options, builtinToolsResolver }) {
	const initialStep = await options?.resolveStep?.();
	const currentModel = initialStep?.model ?? model;
	const request = (initialStep?.chatProvider ?? chatProvider).generation(currentModel);
	const supportedTools = supportsTools(currentModel, request, options);
	const contentArraySupported = supportsContentArray(currentModel, request, options);
	const builtinTools = supportedTools && !initialStep ? await (builtinToolsResolver?.(model, chatProvider) ?? Promise.resolve([])) : [];
	const customTools = supportedTools ? initialStep ? initialStep.tools ?? [] : await resolveTools(options) : [];
	const mergedTools = supportedTools ? [...builtinTools, ...customTools] : [];
	const tools = mergedTools.length > 0 ? mergedTools : void 0;
	const scope = createContinuationScope(request.config, {
		...options,
		providerId: initialStep?.providerId ?? options?.providerId
	});
	return new Promise((resolve, reject) => {
		let settled = false;
		let stepsSettled = false;
		const resolveOnce = () => {
			if (settled) return;
			settled = true;
			resolve();
		};
		const rejectOnce = (error) => {
			if (settled || stepsSettled) return;
			settled = true;
			reject(error);
		};
		const onEvent = async (streamEvent) => {
			try {
				if (streamEvent != null) await options?.onStreamEvent?.(streamEvent);
				if (streamEvent?.type === "error") rejectOnce(streamEvent.error);
			} catch (error) {
				rejectOnce(error);
				if (request.protocol === "responses") throw error;
			}
		};
		try {
			const streamResult = request.protocol === "responses" ? streamResponses({
				config: request.config,
				webSearch: supportedTools && request.webSearch,
				conversation,
				scope,
				options,
				tools,
				initialStep,
				onEvent
			}) : streamChatCompletions({
				config: request.config,
				conversation,
				scope,
				options,
				tools,
				initialStep,
				onEvent,
				supportsContentArray: contentArraySupported
			});
			streamResult.steps.then(async () => {
				if (settled) return;
				stepsSettled = true;
				try {
					const generatedTurn = await streamResult.generatedTurn;
					await options?.onStreamEvent?.({ type: "finish" });
					if (options?.abortSignal?.aborted) throw options.abortSignal.reason;
					await options?.onGeneratedTurn?.(generatedTurn);
				} catch (error) {
					if (!settled) {
						settled = true;
						reject(error);
					}
					return;
				}
				let usage;
				try {
					usage = await streamResult.totalUsage;
				} catch (error) {
					console.error("Stream totalUsage error:", error);
				}
				try {
					const normalizedUsage = !usage || usage.inputTokens == null && usage.outputTokens == null && usage.totalTokens == null ? { source: "unavailable" } : {
						...usage,
						source: "reported"
					};
					await options?.onUsage?.(normalizedUsage);
				} catch (error) {
					console.error("Stream usage callback error:", error);
				}
				resolveOnce();
			}).catch((error) => {
				if (stepsSettled) {
					console.error("Stream usage observation error:", error);
					resolveOnce();
					return;
				}
				rejectOnce(error);
				if (!(error instanceof RequestSwitch)) console.error("Stream steps error:", error);
			});
			streamResult.generatedTurn.catch((error) => {
				if (!(error instanceof RequestSwitch)) console.error("Stream generated turn error:", error);
			});
			streamResult.usage.catch((error) => {
				if (!(error instanceof RequestSwitch)) console.error("Stream usage error:", error);
			});
			streamResult.totalUsage.catch((error) => {
				if (!(error instanceof RequestSwitch)) console.error("Stream totalUsage error:", error);
			});
		} catch (error) {
			rejectOnce(error);
		}
	});
}
/**
* Runs one request and repeats it while the provider reports temporary unavailability.
*
* Retrying is only safe while the consumer has seen no stream event. After the
* first event, text may already be rendered or spoken and tools may already have
* run, so a repeat would duplicate them; those failures surface for manual Retry.
* Cancelling through `abortSignal` also ends a pending retry wait.
*/
async function streamWithTransientRetry(input) {
	for (let attempt = 0;; attempt++) {
		let consumerNotified = false;
		try {
			return await streamOnce({
				...input,
				options: {
					...input.options,
					onStreamEvent: (event) => {
						consumerNotified = true;
						return input.options?.onStreamEvent?.(event);
					}
				}
			});
		} catch (error) {
			const delayMs = consumerNotified ? void 0 : transientRetryDelayMs(error, attempt);
			if (delayMs == null) throw error;
			console.warn(`[llm] Retrying provider request in ${delayMs}ms (retry ${attempt + 1}/${transientRetry.backoffMs.length}):`, error);
			await waitBeforeRetry(delayMs, input.options?.abortSignal);
		}
	}
}
function mergeGenerationUsage(rounds, last) {
	const partial = rounds.flatMap((round) => round.modelCall?.usage ? [round.modelCall.usage] : []);
	if (partial.length === 0) return last;
	return {
		inputTokens: partial.reduce((sum, usage) => sum + usage.inputTokens, last?.inputTokens ?? 0),
		outputTokens: partial.reduce((sum, usage) => sum + usage.outputTokens, last?.outputTokens ?? 0),
		totalTokens: partial.reduce((sum, usage) => sum + usage.totalTokens, last?.totalTokens ?? 0),
		source: "reported"
	};
}
/** Keeps one assistant turn across xsAI tool loops when the next request changes provider scope. */
async function streamFrom(input) {
	if (!input.options?.resolveStep) return streamWithTransientRetry(input);
	const completedRounds = [];
	let turnId = input.options.requestCorrelation?.turnId;
	let request = input;
	let switches = 0;
	while (true) {
		let finalTurn;
		let lastUsage;
		try {
			await streamWithTransientRetry({
				...request,
				options: {
					...request.options,
					generationTurnId: turnId,
					generationRoundOffset: completedRounds.length,
					onGeneratedTurn: (turn) => {
						finalTurn = turn;
					},
					onUsage: (usage) => {
						lastUsage = usage;
					}
				}
			});
			if (finalTurn) await input.options.onGeneratedTurn?.({
				...finalTurn,
				rounds: [...completedRounds, ...finalTurn.rounds]
			});
			const usage = mergeGenerationUsage(completedRounds, lastUsage);
			if (usage) await input.options.onUsage?.(usage);
			return;
		} catch (error) {
			if (!(error instanceof RequestSwitch)) throw error;
			switches += 1;
			if (switches > 10) throw new Error("Generation request scope changed too many times");
			turnId = error.partialTurn.id;
			completedRounds.push(...error.partialTurn.rounds);
			if (completedRounds.length >= 10) throw new Error("Generation tool step limit reached");
			request = {
				...input,
				model: error.next.model,
				chatProvider: error.next.chatProvider,
				options: {
					...input.options,
					providerId: error.next.providerId,
					headers: error.next.headers
				},
				conversation: { turns: [...input.conversation.turns, {
					...error.partialTurn,
					rounds: [...completedRounds]
				}] }
			};
		}
	}
}
const TOOLS_RELATED_ERROR_PATTERNS = [
	/does not support tools/i,
	/no endpoints found that support tool use/i,
	/invalid schema for function/i,
	/invalid.?function.?parameters/i,
	/functions are not supported/i,
	/unrecognized request argument.+tools/i,
	/tool use with function calling is unsupported/i,
	/tool_use_failed/i,
	/does not support function.?calling/i,
	/tools?\s+(is|are)\s+not\s+supported/i
];
function isToolRelatedError(error) {
	const message = String(error);
	return TOOLS_RELATED_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}
const CONTENT_ARRAY_RELATED_ERROR_PATTERNS = [/messages\[\d+\][^"]*invalid type:\s*sequence,\s*expected\s+a\s+string/i, /messages\.\d+\.content[^"]*(?:expected|should be).*string/i];
/**
* Whether the given error indicates the provider rejected content-part arrays
* and the caller should auto-degrade to string-only `content` for this model.
*
* Use when:
* - Catching errors thrown by {@link streamFrom} so the chat store can flip
*   `contentArrayCompatibility` for the failing model key.
*
* Expects:
* - `error` may be an Error instance, a thrown SDK response object, a string,
*   or anything else; we coerce via `String(error)` and pattern-match.
*
* Returns:
* - `true` when the message matches a known "content array unsupported" wire
*   format from an OpenAI-compatible gateway, otherwise `false`.
*/
function isContentArrayRelatedError(error) {
	const message = String(error);
	return CONTENT_ARRAY_RELATED_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}
//#endregion
//#region src/session/merge-loaded-session-messages.ts
function extractMessageContent(message) {
	if (typeof message.content === "string") return message.content;
	if (Array.isArray(message.content)) return message.content.map((part) => {
		if (typeof part === "string") return part;
		if (part && typeof part === "object" && "text" in part) return String(part.text ?? "");
		return "";
	}).join("");
	return "";
}
function getMessageFingerprint(message) {
	return [
		message.id ?? "",
		message.role,
		message.createdAt ?? "",
		extractMessageContent(message)
	].join("");
}
function mergeLoadedSessionMessages(storedMessages, currentMessages) {
	if (currentMessages.length === 0) return storedMessages;
	const currentNonSystemMessages = currentMessages.filter((message, index) => index !== 0 || message.role !== "system");
	if (currentNonSystemMessages.length === 0) return storedMessages;
	const seen = new Set(storedMessages.map(getMessageFingerprint));
	const extraMessages = currentNonSystemMessages.filter((message) => {
		const fingerprint = getMessageFingerprint(message);
		if (seen.has(fingerprint)) return false;
		seen.add(fingerprint);
		return true;
	});
	if (extraMessages.length === 0) return storedMessages;
	const systemMessage = storedMessages[0]?.role === "system" ? storedMessages[0] : currentMessages[0]?.role === "system" ? currentMessages[0] : void 0;
	if (storedMessages.length === 0 && systemMessage) return [systemMessage, ...extraMessages];
	return [...storedMessages, ...extraMessages];
}
//#endregion
export { annotateToolCallResult, buildContextPromptMessage, categorizeResponse, chatContentToInputSegments, chatMessagesToTurns, conversationToChatMessages, createChatHooks, createChatOrchestratorRuntime, createContextRegistry, createStreamingCategorizer, formatContextPromptText, formatTimePrefix, isContentArrayRelatedError, isToolRelatedError, mergeLoadedSessionMessages, modelKey, renderConversationPreview, streamFrom, useLlmmarkerParser };
